import React, { useEffect, useMemo, useRef } from 'react';
import moment from 'moment';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { graphColors, palette } from 'shared/constants/colorsConstants';
import { BarsChartProps, CustomTooltipContentProps, DataPoint } from '../../../types.ts';
import { CustomTooltip } from './CustomTooltip.tsx';
import CustomYAxisTick from './CustomYAxisTick.tsx';
import styles from './barsChart.module.scss';
import { formatQuarterlyDate } from './utils.ts';

const xTickDateFormat = (value, xTickFormat, isDateChart = false) => {
  if (!xTickFormat || !isDateChart) {
    return value;
  }
  switch (xTickFormat) {
    case 'daily':
      return moment(value).format('MMM DD');
    case 'monthly':
      return moment(value).format('MMM');
    case 'quarterly':
      return formatQuarterlyDate(value);
  }
};

const CustomYAxisTickComponent = ({
  x,
  y,
  payload,
  fill,
}: {
  x: number;
  y: number;
  payload: { value: string | number };
  fill: string;
}) => <CustomYAxisTick x={x} y={y} payload={payload} fill={fill} />;

const CustomYAxisTickWrapper = (props: { x: number; y: number; payload: { value: string | number } }) => {
  return <CustomYAxisTickComponent {...props} fill={palette.gray[450]} />;
};

const CustomTooltipContent: React.FC<CustomTooltipContentProps> = ({ formatType, payload }) => {
  if (!payload) return null;
  return <CustomTooltip formatType={formatType} payload={payload} />;
};
const BarsChart: React.FC<BarsChartProps> = ({
  data,
  dataKeys,
  dataProperties,
  specialGradient,
  xKey,
  xTickFormat,
  height = 350,
  barWidth,
  isDateChart = false,
  focusToStart = false,
}) => {
  const barWrapperRef = useRef<HTMLDivElement>(null);

  const calculateChartWidth = useMemo(() => {
    const maxTextLength = data.reduce((max, item) => {
      const value = item[xKey];
      const length = typeof value === 'string' ? value.length : 0;
      return Math.max(max, length);
    }, 0);
    if (maxTextLength < 20 && data?.length * dataKeys?.length < 20) {
      return '100%';
    }
    if (barWidth) {
      return barWidth * data.length;
    }
    return maxTextLength * 7 * data.length;
  }, [data, xKey, barWidth, dataKeys?.length]);

  const chartKey = useMemo(() => {
    return data.length > 0 ? `${data.length}-${data[0][xKey]}` : 'empty';
  }, [data, xKey]);

  useEffect(() => {
    if (focusToStart && barWrapperRef.current) {
      barWrapperRef.current.scrollLeft = barWrapperRef.current.scrollWidth;
    }
  }, [data, focusToStart]);
  return (
    <div className={styles.barWrapper} ref={barWrapperRef}>
      {data?.length > 0 ? (
        <div style={{ width: calculateChartWidth ?? '100%' }}>
          <ResponsiveContainer width="100%" height={height ?? 300}>
            <BarChart
              key={chartKey}
              data={data}
              margin={{
                top: 15,
                right: 30,
                left: 20,
                bottom: -10,
              }}
            >
              {specialGradient}
              <CartesianGrid stroke={palette.gray[100]} vertical={false} />
              <XAxis
                dataKey={xKey}
                tickCount={0}
                axisLine={false}
                tickLine={false}
                interval={0}
                tickFormatter={(value) => xTickDateFormat(value, xTickFormat, isDateChart)}
              />
              <YAxis type="number" axisLine={false} tickLine={false} tick={CustomYAxisTickWrapper} />
              <Tooltip
                itemSorter={() => 1}
                cursor={{ fill: 'transparent' }}
                content={(props) => (
                  <CustomTooltipContent
                    formatType={xTickFormat}
                    payload={props.payload as { dataKey: string; value: number; color: string; payload: DataPoint }[]}
                  />
                )}
              />

              {dataKeys?.map((dataKey, index) => (
                <Bar
                  key={dataKey}
                  barSize={28}
                  dataKey={dataKey}
                  fill={dataProperties?.[dataKey]?.fill ?? graphColors[index]}
                  stackId={dataProperties?.[dataKey]?.stackId ?? 'a'}
                >
                  {data.map(() => {
                    return <Cell key={`cell-${dataKey}`} radius={5} />;
                  })}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <span className={styles.noData}>No Data</span>
      )}
    </div>
  );
};

export default BarsChart;
